<template>
  <div :style="themeCss">
    <div id="aw--c">
      <div id="aw-sl">
        <div
          v-if="videoInfo != undefined"
          :key="videoInfo.video_urls.dash_url"
          class="amplily-player"
        >
          <div class="rnd-crns">
            <VideoPlayer
              :poster="videoInfo.video_urls.poster_url"
              :video_urls="videoInfo.video_urls"
              :mid="videoInfo.moment_id"
              :subtitleUrls="videoInfo.subtitle_urls"
              :subtitles="videoInfo.subtitles"
              :continuefromTime="videoInfo.end_time"
              :duration="videoInfo.duration"
              :videoType="videoType"
              v-if="
                !videoInfo.video_urls.iframe_url ||
                videoInfo.video_urls.iframe_url == ''
              "
            />
            <IframeVideoPlayer
              v-else
              :iframe_url="videoInfo.video_urls.iframe_url"
              :poster="videoInfo.video_urls.poster_url"
              :duration="videoInfo.duration"
            />
          </div>
          <div id="redirect_btn" v-if="redirect_url">
            <p>
              <a :href="redirect_url" v-html="redirectText"></a>
            </p>
          </div>
        </div>
        <div v-else>
          {{ loadingmsg }}
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import VideoPlayer from "../components/shared/Player.vue"
import IframeVideoPlayer from "../components/shared/IframeVideoPlayer.vue"
import "../assets/_base.scss"
import { SingleVideoInfo } from "../models/SingleVideoInfo"
import { AwEventStore } from "../models/AwEventStore"
import { AwEventType } from "../models/AwEventType"
import setThemeData from "../services/setThemeData"
import Color from "../services/Color"
import { AnalyticsClient } from "@/services/AnalyticsService"
import { WebinarDataService } from "@/services/WebinarDataServices"
import { container } from "tsyringe"
import { computed, defineComponent, ref } from "@vue/runtime-core"
import injectStrict from "@/utils/injectStrict"
import { GlobalDataKey, LoggerKey } from "@/symbols"
import getVideoType from "@/utils/getVideoType"
import { VideoType } from "@/models/VideoType"
export default defineComponent({
  components: { VideoPlayer, IframeVideoPlayer },
  props: {
    fetchId: { type: String, required: true }
  },
  setup(props) {
    const globalData = injectStrict(GlobalDataKey).value
    const logger = injectStrict(LoggerKey)
    const videoType: VideoType | undefined = getVideoType(props.fetchId)
    const webinarId = ref(-1)
    const videoInfo = ref<SingleVideoInfo | null>(null)
    const loadingmsg = ref("Loading video...")
    const widgetHeight = computed<number>(() => globalData.height)
    let redirect_url = computed(() => globalData.redirect_data.url)
    let redirectText = computed(() => globalData.redirect_data.text)
    const themeCss = computed(() => {
      let color = new Color(globalData.theme_data.theme_color)
      color.a = 0.4
      return {
        "--font-color": globalData.theme_data.font_color,
        "--font-size": globalData.theme_data.font_size + "pt", //default is 11pt
        "--font-family": globalData.theme_data.font_family,
        "--theme-color": globalData.theme_data.theme_color,
        "--theme-contrast": color.islight() ? `#000000` : `#ffffff`,
        "--theme-contrast-brightness": color.islight()
          ? `rgba(0,0,0,0.2)`
          : `rgba(255,255,255,0.2)`,
        "--font-size-22":
          (globalData.theme_data.font_size + 11).toString() + "pt",
        "--font-size-12":
          (globalData.theme_data.font_size + 1).toString() + "pt",
        "--font-size-14":
          (globalData.theme_data.font_size + 3).toString() + "pt",
        "--font-size-18":
          (globalData.theme_data.font_size + 7).toString() + "pt",
        "--colored-playicon": `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='24' viewBox='0 0 24 24' width='24'%3E%3Cpath d='M8 5v14l11-7z' fill='${
          color.islight() ? `%23000000` : `%23ffffff`
        }'/%3E%3Cpath d='M0 0h24v24H0z' fill='none'/%3E%3C/svg%3E")`
      }
    })
    const dataService = container.resolve(WebinarDataService)
    const analytics = container.resolve(AnalyticsClient)
    return {
      dataService,
      widgetHeight,
      themeCss,
      globalData,
      webinarId,
      videoInfo,
      loadingmsg,
      analytics,
      redirect_url,
      logger,
      redirectText,
      videoType
    }
  },
  created() {
    this.dataService.getData(this.fetchId).then(returnValue => {
      this.loadingmsg = "Couldn't load the video."
      if (returnValue == undefined) {
        this.logger!.error(`Parmonic Widget error: Video not found`)
        return
      }
      this.videoInfo = returnValue as SingleVideoInfo
      this.webinarId = this.videoInfo.id
      setThemeData(this.videoInfo.theme_data, this.globalData)
      this.analytics.setUp(
        this.webinarId,
        this.globalData.analyticsEventsCaptureEndPoint,
        this.logger
      )
      var event: AwEventStore = {
        eventType: AwEventType.WidgetViewed,
        videoType: this.videoType,
        momentId: this.videoInfo.moment_id
          ? parseInt(this.videoInfo.moment_id)
          : undefined
      }
      this.analytics.register(event)
    })
  }
})
</script>
<style scoped>
.amplily-player {
  width: 100% !important;
}
#redirect_btn {
  width: fit-content;
  padding: 2px;
  margin-top: 5px;
  margin-left: 50%;
  transform: translateX(-50%);
  background-color: var(--theme-color);
  box-shadow: 0px 0px 1px 1px var(--theme-contrast-brightness);
  border-radius: 5px;
}
#redirect_btn > p {
  margin: 0em;
  padding: 5px;
  cursor: pointer;
}
#redirect_btn > p > a {
  color: var(--theme-contrast);
  text-decoration: inherit;
  font-family: var(--font-family);
}
</style>
